import { useEffect, useState } from "react";

export type FileContentState = { status: "loading" } | { status: "success"; value: string } | { status: "error" };

export function useFileContent(
  content: string | Uint8Array,
  enabled = true,
  decode: (bytes: Uint8Array) => string = decodeUtf8,
): FileContentState {
  const [fileContentState, setFileContentState] = useState<FileContentState>({ status: "loading" });

  useEffect(() => {
    if (!enabled) return;

    if (ArrayBuffer.isView(content)) {
      try {
        setFileContentState({ status: "success", value: decode(content) });
      } catch {
        setFileContentState({ status: "error" });
      }
      return;
    }
    setFileContentState({ status: "loading" });
    const abortController = new AbortController();
    fetch(content, { signal: abortController.signal })
      .then((result) => result.arrayBuffer())
      .then((buffer) => decode(new Uint8Array(buffer)))
      .then(
        (text) => {
          if (abortController.signal.aborted) {
            return;
          }
          setFileContentState({ status: "success", value: text });
        },
        () => {
          if (abortController.signal.aborted) {
            return;
          }
          setFileContentState({ status: "error" });
        },
      );

    return () => abortController.abort();
  }, [content, enabled, decode]);

  if (typeof content === "string") {
    return fileContentState;
  }

  return fileContentState;
}

function decodeUtf8(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes);
}
